import { memo, useState } from 'react'
import KanbanCard from './KanbanCard'
import { sileo } from 'sileo'

/**
 * Representa una columna individual dentro del tablero Kanban.
 * * Este componente actúa como contenedor de las tarjetas (`KanbanCard`) y gestiona
 * el área de soltado (drop zone). Incluye estados visuales para cuando un elemento
 * es arrastrado sobre ella y validaciones para habilitar o deshabilitar el movimiento.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {string|number} props.columnKey - Identificador único de la columna (ej: '1', 'todo').
 * @param {string} props.title - Título descriptivo de la columna que se muestra en el header.
 * @param {Array<Object>} [props.cards=[]] - Arreglo de objetos de tickets/tarjetas pertenecientes a esta columna.
 * @param {Function} props.onDrop - Callback ejecutado cuando se suelta una tarjeta en esta columna. Recibe el `columnKey`.
 * @param {Function} props.onDragStart - Callback pasado a las tarjetas para iniciar el proceso de arrastre.
 * @param {Function} props.onSelect - Callback ejecutado al hacer clic en una tarjeta para ver sus detalles.
 * @param {boolean} [props.disableDrag=false] - Si es verdadero, bloquea cualquier interacción de movimiento y muestra advertencias.
 * @param {Record<string, Object>} [props.authors={}] - Empleados por auth id, para mostrar el autor de cada tarjeta.
 * * @returns {JSX.Element} Una columna de Kanban con scroll interno y estados de interacción.
 */
const KanbanColumn = ({
  columnKey,
  title,
  cards = [],
  onDrop,
  onDragStart,
  onSelect,
  disableDrag = false,
  authors = {},
  color,
}) => {
  /** @type {[boolean, Function]} Estado para controlar si hay un elemento siendo arrastrado sobre la columna */
  const [isOver, setIsOver] = useState(false)

  /**
   * Maneja el evento final de soltar un elemento en la columna.
   * Si el drag está deshabilitado, utiliza la librería `sileo` para mostrar una notificación.
   * @param {DragEvent} e - Evento nativo de drag and drop.
   */
  const handleDrop = (e) => {
    if (disableDrag) {
      sileo.warning({
        title: 'Arrastrar y soltar está deshabilitado',
        description: 'No puedes mover los tickets en esta vista.',
      })

      return
    }
    e.preventDefault()
    setIsOver(false)
    onDrop(columnKey)
  }

  return (
    <div
      className="flex-fill d-flex h-100"
      style={{ minWidth: '250px' }}
      onDragOver={
        disableDrag
          ? undefined
          : (e) => {
              e.preventDefault()
              setIsOver(true)
            }
      }
      onDragLeave={disableDrag ? undefined : () => setIsOver(false)}
      onDrop={disableDrag ? undefined : handleDrop}
    >
      <div
        className={`custom-kanban-column w-100 d-flex flex-column transition-all ${
          isOver ? 'is-over' : ''
        }`}
      >
        {/* Header de Columna */}
        <div className="column-head">
          <span
            className="column-dot"
            style={{ background: color || 'var(--gm-text-faint)' }}
          />
          <h6 className="column-title mb-0">{title || columnKey}</h6>
          <span className="badge-count">{cards.length}</span>
        </div>

        {/* Contenedor de Cards */}
        <div className="cards-scroll-area flex-grow-1 overflow-auto d-flex flex-column gap-2">
          {cards.length === 0 ? (
            <div className="empty-state-column">Sin tickets</div>
          ) : (
            cards.map((card) => (
              <KanbanCard
                key={card.id}
                card={card}
                columnKey={columnKey}
                onDragStart={disableDrag ? undefined : onDragStart}
                onSelect={onSelect}
                author={authors[card.userId]}
              />
            ))
          )}
        </div>
      </div>

      <style>{`
        .transition-all { transition: background 0.2s ease, border-color 0.2s ease; }

        /* Columnas transparentes; el divisor vertical lo pone el tablero. */
        .custom-kanban-column {
          background: transparent;
          border-radius: 10px;
          padding: 0 10px;
          min-height: 0;
        }
        .custom-kanban-column.is-over {
          background: var(--gm-primary-soft);
        }

        .column-head {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 2px 10px;
        }
        .column-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .column-title {
          flex: 1;
          font-weight: 600;
          font-size: 0.72rem;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: var(--gm-text-muted);
        }
        .badge-count {
          min-width: 22px;
          padding: 1px 7px;
          border-radius: 99px;
          text-align: center;
          font-size: 0.7rem;
          font-weight: 600;
          color: var(--gm-text-muted);
          background: var(--gm-surface-2);
          font-variant-numeric: tabular-nums;
        }

        .empty-state-column {
          text-align: center;
          color: var(--gm-text-faint);
          font-size: 0.75rem;
          padding: 14px 8px;
          border: 1px dashed var(--gm-border);
          border-radius: 8px;
        }

        .cards-scroll-area::-webkit-scrollbar { width: 5px; }
        .cards-scroll-area::-webkit-scrollbar-thumb {
          background: var(--gm-border);
          border-radius: 10px;
        }
        .cards-scroll-area::-webkit-scrollbar-thumb:hover {
          background: var(--gm-text-faint);
        }
      `}</style>
    </div>
  )
}

export default memo(KanbanColumn)
